import {Inject, Injectable, PLATFORM_ID, signal} from '@angular/core';
import {ToolsService} from './tools.service';
import {RoutesEnums} from '../enums/RoutesEnums';
import {PuffsEnum} from '../enums/PuffsEnum';
import {forkJoin, Observable, of} from 'rxjs';
import {WPPuffsListItem} from '../types/WPPuffsListItem';
import {WPPostBlog} from '../types/WPPostBlog';
import {WPPostNative} from '../types/WPPostNative';
import {WPPostGuide} from '../types/WPPostGuide';
import {WPPageCustom} from '../types/WPPageCustom';
import {catchError, map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {AppConfigService} from './app-config.service';
import {WPPostCareServiceTypePage} from '../modules/search/modules/search-content-pages-v2/typings/WPPostCareServiceTypePage';
import {WPPostSymptomPage} from '../modules/search/modules/search-content-pages-v2/typings/WPPostSymptomPage';

interface CountedPuffs {
  [k: string]: number;
}

@Injectable()
export class StaticSidebarPuffsService {
  public puffs = signal<WPPuffsListItem[]>(null);
  private blogPuffs: WPPostBlog[] = [];
  private nativePuffs: WPPostNative[] = [];
  private guidePuffs: WPPostGuide[] = [];
  private careServiceTypePagePuffs: WPPostCareServiceTypePage[] = [];
  private symptomPagePuffs: WPPostSymptomPage[] = [];
  private loadedIsCustomer: boolean;
  private loadedModuleName: RoutesEnums;
  private loadedCpruTypes: number[];
  private loadedServiceTypes: number[];
  private loadedSymptoms: number[];
  private loadedExcludedIds: number[];

  constructor(
    private appConfigService: AppConfigService,
    private tools: ToolsService,
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {}

  public reloadCombinedPuffs(
    moduleName: RoutesEnums,
    isCustomer: boolean,
    cpruTypes: number[],
    serviceTypes: number[],
    symptoms: number[],
    excludedIds: number[],
  ): void {
    if (
      this.loadedIsCustomer !== isCustomer ||
      this.loadedModuleName !== moduleName ||
      this.tools.equals(this.loadedCpruTypes, cpruTypes) === false ||
      this.tools.equals(this.loadedExcludedIds, excludedIds) === false ||
      this.tools.equals(this.loadedSymptoms, symptoms) === false ||
      this.tools.equals(this.loadedServiceTypes, serviceTypes) === false
    ) {
      this.blogPuffs = [];
      this.nativePuffs = [];
      this.guidePuffs = [];
      this.careServiceTypePagePuffs = [];
      this.symptomPagePuffs = [];
      this.loadedIsCustomer = isCustomer;
      this.loadedModuleName = moduleName;
      this.loadedCpruTypes = cpruTypes;
      this.loadedServiceTypes = serviceTypes;
      this.loadedSymptoms = symptoms;
      this.loadedExcludedIds = excludedIds;

      const puffsGridConfig: PuffsEnum[] = this.getPuffsGridConfig(moduleName, isCustomer);
      const countedPuffs: CountedPuffs = this.getCountedPuffsTypes(puffsGridConfig);

      // Create observables for each type of content
      const observables: Observable<any>[] = [];

      // Load native content for cpruTypes
      if (!this.tools.isNil(cpruTypes) && cpruTypes.length > 0 && countedPuffs[PuffsEnum.NATIVE] > 0) {
        cpruTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostNative[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-native-care_provider_unit_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.NATIVE, data})),
                catchError((error) => of({type: PuffsEnum.NATIVE, data: []})),
              ),
          );
        });
      }

      // Load symptompage content for cpruTypes
      if (!this.tools.isNil(cpruTypes) && cpruTypes.length > 0 && countedPuffs[PuffsEnum.SYMPTOM_PAGE] > 0) {
        cpruTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostSymptomPage[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-symptompage-care_provider_unit_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.SYMPTOM_PAGE, data})),
                catchError((error) => of({type: PuffsEnum.SYMPTOM_PAGE, data: []})),
              ),
          );
        });
      }

      // Load careservicepage content for cpruTypes
      if (!this.tools.isNil(cpruTypes) && cpruTypes.length > 0 && countedPuffs[PuffsEnum.CARE_SERVICE_TYPE_PAGE] > 0) {
        cpruTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostCareServiceTypePage[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-careservicepage-care_provider_unit_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.CARE_SERVICE_TYPE_PAGE, data})),
                catchError((error) => of({type: PuffsEnum.CARE_SERVICE_TYPE_PAGE, data: []})),
              ),
          );
        });
      }

      // Load blog content for cpruTypes
      if (!this.tools.isNil(cpruTypes) && cpruTypes.length > 0 && countedPuffs[PuffsEnum.BLOG] > 0) {
        cpruTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostBlog[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-blog-care_provider_unit_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.BLOG, data})),
                catchError((error) => of({type: PuffsEnum.BLOG, data: []})),
              ),
          );
        });
      }

      // Load guide content for cpruTypes
      if (!this.tools.isNil(cpruTypes) && cpruTypes.length > 0 && countedPuffs[PuffsEnum.GUIDE] > 0) {
        cpruTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostGuide[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-guide-care_provider_unit_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.GUIDE, data})),
                catchError((error) => of({type: PuffsEnum.GUIDE, data: []})),
              ),
          );
        });
      }

      // Load native content for serviceTypes
      if (!this.tools.isNil(serviceTypes) && serviceTypes.length > 0 && countedPuffs[PuffsEnum.NATIVE] > 0) {
        serviceTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostNative[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-native-care_service_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.NATIVE, data})),
                catchError((error) => of({type: PuffsEnum.NATIVE, data: []})),
              ),
          );
        });
      }

      // Load symptompage content for serviceTypes
      if (!this.tools.isNil(serviceTypes) && serviceTypes.length > 0 && countedPuffs[PuffsEnum.SYMPTOM_PAGE] > 0) {
        serviceTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostSymptomPage[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-symptompage-care_service_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.SYMPTOM_PAGE, data})),
                catchError((error) => of({type: PuffsEnum.SYMPTOM_PAGE, data: []})),
              ),
          );
        });
      }

      // Load careservicepage content for serviceTypes
      if (
        !this.tools.isNil(serviceTypes) &&
        serviceTypes.length > 0 &&
        countedPuffs[PuffsEnum.CARE_SERVICE_TYPE_PAGE] > 0
      ) {
        serviceTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostCareServiceTypePage[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-careservicepage-care_service_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.CARE_SERVICE_TYPE_PAGE, data})),
                catchError((error) => of({type: PuffsEnum.CARE_SERVICE_TYPE_PAGE, data: []})),
              ),
          );
        });
      }

      // Load blog content for serviceTypes
      if (!this.tools.isNil(serviceTypes) && serviceTypes.length > 0 && countedPuffs[PuffsEnum.BLOG] > 0) {
        serviceTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostBlog[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-blog-care_service_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.BLOG, data})),
                catchError((error) => of({type: PuffsEnum.BLOG, data: []})),
              ),
          );
        });
      }

      // Load guide content for serviceTypes
      if (!this.tools.isNil(serviceTypes) && serviceTypes.length > 0 && countedPuffs[PuffsEnum.GUIDE] > 0) {
        serviceTypes.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostGuide[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-guide-care_service_type-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.GUIDE, data})),
                catchError((error) => of({type: PuffsEnum.GUIDE, data: []})),
              ),
          );
        });
      }

      // Load native content for symptoms
      if (!this.tools.isNil(symptoms) && symptoms.length > 0 && countedPuffs[PuffsEnum.NATIVE] > 0) {
        symptoms.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostNative[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-native-symptom-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.NATIVE, data})),
                catchError((error) => of({type: PuffsEnum.NATIVE, data: []})),
              ),
          );
        });
      }

      // Load symptompage content for symptoms
      if (!this.tools.isNil(symptoms) && symptoms.length > 0 && countedPuffs[PuffsEnum.SYMPTOM_PAGE] > 0) {
        symptoms.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostSymptomPage[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-symptompage-symptom-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.SYMPTOM_PAGE, data})),
                catchError((error) => of({type: PuffsEnum.SYMPTOM_PAGE, data: []})),
              ),
          );
        });
      }

      // Load careservicepage content for symptoms
      if (!this.tools.isNil(symptoms) && symptoms.length > 0 && countedPuffs[PuffsEnum.CARE_SERVICE_TYPE_PAGE] > 0) {
        symptoms.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostCareServiceTypePage[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-careservicepage-symptom-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.CARE_SERVICE_TYPE_PAGE, data})),
                catchError((error) => of({type: PuffsEnum.CARE_SERVICE_TYPE_PAGE, data: []})),
              ),
          );
        });
      }

      // Load blog content for symptoms
      if (!this.tools.isNil(symptoms) && symptoms.length > 0 && countedPuffs[PuffsEnum.BLOG] > 0) {
        symptoms.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostBlog[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-blog-symptom-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.BLOG, data})),
                catchError((error) => of({type: PuffsEnum.BLOG, data: []})),
              ),
          );
        });
      }

      // Load guide content for symptoms
      if (!this.tools.isNil(symptoms) && symptoms.length > 0 && countedPuffs[PuffsEnum.GUIDE] > 0) {
        symptoms.forEach((id) => {
          observables.push(
            this.http
              .get<
                WPPostGuide[]
              >(`${this.appConfigService.appConfig.amazonS3Url}wp/related/related-guide-symptom-${id}.json`)
              .pipe(
                map((data) => ({type: PuffsEnum.GUIDE, data})),
                catchError((error) => of({type: PuffsEnum.GUIDE, data: []})),
              ),
          );
        });
      }

      // If no observables were created, set empty puffs and return
      if (observables.length === 0) {
        this.puffs.set([]);
        return;
      }

      // Execute all observables and process the results
      forkJoin(observables).subscribe(
        (results) => {
          // Process results by type
          results.forEach((result) => {
            if (result.data && result.data.length > 0) {
              const filteredData = result.data.filter((item) => this.filterPost(item));

              switch (result.type) {
                case PuffsEnum.BLOG:
                  this.blogPuffs = [...this.blogPuffs, ...filteredData];
                  break;
                case PuffsEnum.GUIDE:
                  this.guidePuffs = [...this.guidePuffs, ...filteredData];
                  break;
                case PuffsEnum.NATIVE:
                  this.nativePuffs = [...this.nativePuffs, ...filteredData];
                  break;
                case PuffsEnum.CARE_SERVICE_TYPE_PAGE:
                  this.careServiceTypePagePuffs = [...this.careServiceTypePagePuffs, ...filteredData];
                  break;
                case PuffsEnum.SYMPTOM_PAGE:
                  this.symptomPagePuffs = [...this.symptomPagePuffs, ...filteredData];
                  break;
              }
            }
          });

          // Filter out excluded IDs
          if (!this.tools.isNil(excludedIds) && excludedIds.length > 0) {
            this.blogPuffs = this.blogPuffs.filter((item) => !excludedIds.includes(item.id));
            this.guidePuffs = this.guidePuffs.filter((item) => !excludedIds.includes(item.id));
            this.nativePuffs = this.nativePuffs.filter((item) => !excludedIds.includes(item.id));
            this.careServiceTypePagePuffs = this.careServiceTypePagePuffs.filter(
              (item) => !excludedIds.includes(item.id),
            );
            this.symptomPagePuffs = this.symptomPagePuffs.filter((item) => !excludedIds.includes(item.id));
          }

          // Randomize arrays if there are more items than needed
          this.randomizeArrayIfNeeded(this.blogPuffs, countedPuffs[PuffsEnum.BLOG]);
          this.randomizeArrayIfNeeded(this.guidePuffs, countedPuffs[PuffsEnum.GUIDE]);
          this.randomizeArrayIfNeeded(this.nativePuffs, countedPuffs[PuffsEnum.NATIVE]);
          this.randomizeArrayIfNeeded(this.careServiceTypePagePuffs, countedPuffs[PuffsEnum.CARE_SERVICE_TYPE_PAGE]);
          this.randomizeArrayIfNeeded(this.symptomPagePuffs, countedPuffs[PuffsEnum.SYMPTOM_PAGE]);

          this.preparePuffsArray(moduleName, isCustomer);
        },
        (error) => {
          console.error('Error loading puffs:', error);
          this.puffs.set([]);
        },
      );
    }
  }

  private randomizeArrayIfNeeded<T>(array: T[], neededCount: number): void {
    if (array.length > neededCount) {
      // Shuffle the array using Fisher-Yates algorithm
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      // Truncate the array to the needed count
      array.length = neededCount;
    }
  }

  private preparePuffsArray(moduleName: RoutesEnums, isCustomer: boolean): void {
    const puffsGridConfig = this.getPuffsGridConfig(moduleName, isCustomer);
    const puffData = {
      [PuffsEnum.BLOG]: this.blogPuffs,
      [PuffsEnum.GUIDE]: this.guidePuffs,
      [PuffsEnum.NATIVE]: this.nativePuffs,
      [PuffsEnum.CARE_SERVICE_TYPE_PAGE]: this.careServiceTypePagePuffs,
      [PuffsEnum.SYMPTOM_PAGE]: this.symptomPagePuffs,
    };

    const getPuff = (puffType, puffArray) => {
      if (puffArray.length > 0) {
        return {puffType, puffContent: puffArray.shift()};
      }
      return null;
    };

    const puffs = puffsGridConfig
      .map((puffType) => getPuff(puffType, puffData[puffType]))
      .filter((puff) => puff !== null);

    this.puffs.set(puffs);
  }

  private getPuffsGridConfig(moduleName: RoutesEnums, isCustomer?: boolean): PuffsEnum[] {
    const puffsGridConfig: PuffsEnum[] = [];

    switch (moduleName) {
      case RoutesEnums.SEARCH:
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.CARE_SERVICE_TYPE_PAGE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
        puffsGridConfig.push(PuffsEnum.BLOG);
        break;
      case RoutesEnums.CAREPROVIDER:
        if (isCustomer === true) {
          puffsGridConfig.push(PuffsEnum.CARE_SERVICE_TYPE_PAGE);
          puffsGridConfig.push(PuffsEnum.CARE_SERVICE_TYPE_PAGE);
          puffsGridConfig.push(PuffsEnum.CARE_SERVICE_TYPE_PAGE);
          puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
          puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
          puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
        } else {
          puffsGridConfig.push(PuffsEnum.CARE_SERVICE_TYPE_PAGE);
          puffsGridConfig.push(PuffsEnum.NATIVE);
          puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
          puffsGridConfig.push(PuffsEnum.NATIVE);
          puffsGridConfig.push(PuffsEnum.NATIVE);
        }
        break;
      case RoutesEnums.GUIDE:
        puffsGridConfig.push(PuffsEnum.CARE_SERVICE_TYPE_PAGE);
        puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
        puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        break;
      case RoutesEnums.BLOG:
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
        puffsGridConfig.push(PuffsEnum.CARE_SERVICE_TYPE_PAGE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        break;
      default:
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
        puffsGridConfig.push(PuffsEnum.CARE_SERVICE_TYPE_PAGE);
        puffsGridConfig.push(PuffsEnum.NATIVE);
        puffsGridConfig.push(PuffsEnum.SYMPTOM_PAGE);
        break;
    }
    return puffsGridConfig;
  }

  private getCountedPuffsTypes(puffsGridConfig: PuffsEnum[]): CountedPuffs {
    let countedPuffs: CountedPuffs = {
      [PuffsEnum.BLOG]: 0,
      [PuffsEnum.GUIDE]: 0,
      [PuffsEnum.NATIVE]: 0,
      [PuffsEnum.PAGE]: 0,
      [PuffsEnum.CARE_SERVICE_TYPE_PAGE]: 0,
      [PuffsEnum.SYMPTOM_PAGE]: 0,
    };
    puffsGridConfig.forEach((puff) => {
      countedPuffs[puff]++;
    });
    return countedPuffs;
  }

  private filterPost(
    item: WPPostBlog | WPPostGuide | WPPostNative | WPPageCustom | WPPostCareServiceTypePage | WPPostSymptomPage,
  ): boolean {
    return item.acf.isPreview === false || item.acf.isPreview == null;
  }
}
